import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    IconButton,
    Button,
    Select,
    MenuItem,
    Tooltip,
    Chip,
    useTheme,
    useMediaQuery,
    styled,
    Badge,
    Avatar,
    Divider,
    LinearProgress,
    FormControl,
    CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ClassIcon from '@mui/icons-material/Class';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import { YoklamaSistemiResponseModal } from '../../types/modals';
import { YOKLAMA_DURUMLARI } from '../../constants/yoklamaDurumlari';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[3],
    transition: 'box-shadow 0.3s',
    '&:hover': {
        boxShadow: theme.shadows[6],
    },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const StudentRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
    transition: 'background-color 0.2s',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const StatusSelect = styled(FormControl)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius,
        transition: 'all 0.2s',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
        },
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: theme.shadows[2],
    transition: 'all 0.2s',
    '&:hover': {
        boxShadow: theme.shadows[4],
        transform: 'translateY(-2px)',
    },
    '&:active': {
        transform: 'translateY(0)',
    },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    fontWeight: 'medium',
    transition: 'transform 0.2s',
    '&:hover': {
        transform: 'scale(1.05)',
    },
}));

interface DersKartProps {
    ders: YoklamaSistemiResponseModal;
    isExpanded: boolean;
    selectedDurum: Record<number, Record<number, string>>;
    kayitYapiliyor: boolean;
    onToggle: () => void;
    onDurumChange: (dersId: number, ogrenciId: number, durum: string) => void;
    onYoklamaAl: () => void;
    isYoklamaEnabled: boolean; 
}

export const DersKart: React.FC<DersKartProps> = ({
    ders,
    isExpanded,
    selectedDurum,
    kayitYapiliyor,
    onToggle,
    onDurumChange,
    onYoklamaAl,
    isYoklamaEnabled
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const dersDurumlari = selectedDurum[ders.id] || {};
    
    // Calculate completion percentage
    const completionPercentage = ders.ogrenciDurumListesi.length > 0
        ? (Object.keys(dersDurumlari).length / ders.ogrenciDurumListesi.length) * 100
        : 0;
    
    // Get color based on completion
    const getProgressColor = () => {
        if (completionPercentage < 30) return theme.palette.error.main;
        if (completionPercentage < 70) return theme.palette.warning.main;
        return theme.palette.success.main;
    };
    
    // Get status color based on selected status
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'G': return theme.palette.success.main;
            case 'I': return theme.palette.error.main;
            case 'R': return theme.palette.warning.main;
            default: return theme.palette.grey[500];
        }
    };
    
    // Get status label
    const getStatusLabel = (status: string) => {
        const found = YOKLAMA_DURUMLARI.find(durum => durum.value === status);
        return found ? found.label : 'Seçiniz';
    };
    
    return (
        <StyledPaper>
            <HeaderBox>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <ClassIcon />
                            <Typography variant="subtitle1" fontWeight="medium">
                                {ders.dersAdi}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <AccessTimeIcon fontSize="small" />
                            <Typography variant="body2">
                                {ders.saat}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Badge 
                                badgeContent={ders.ogrenciDurumListesi.length} 
                                color="secondary"
                                max={99}
                            >
                                <PersonIcon />
                            </Badge>
                            <Typography variant="body2">
                                {ders.sinifAdi}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
                <Tooltip title={isExpanded ? "Daralt" : "Genişlet"}>
                    <IconButton
                        size="small"
                        sx={{ 
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.1)',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.2)',
                            }
                        }}
                        onClick={onToggle}
                    >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Tooltip>
            </HeaderBox>
            
            {isExpanded && (
                <>
                    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Öğrenci
                                </Typography>
                            </Grid>
                            {!isMobile && (
                                <Grid item xs={3}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Sınıf Bilgisi
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={isMobile ? 6 : 3}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Durum
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    
                    <Divider />
                    
                    <Box sx={{ maxHeight: '400px', overflowY: 'auto', px: 2 }}>
                        {ders.ogrenciDurumListesi.map((ogrenci) => (
                            <StudentRow key={ogrenci.ogrenciId}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={6}>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Avatar 
                                                sx={{ 
                                                    width: 36, 
                                                    height: 36, 
                                                    bgcolor: dersDurumlari[ogrenci.ogrenciId] 
                                                        ? getStatusColor(dersDurumlari[ogrenci.ogrenciId])
                                                        : theme.palette.grey[300]
                                                }}
                                            >
                                                {ogrenci.adSoyad.charAt(0)}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {ogrenci.adSoyad}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {ogrenci.ogrenciNo}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    
                                    {!isMobile && (
                                        <Grid item xs={3}>
                                            <Tooltip title={`${ogrenci.bolum} - ${ogrenci.seviye}`}>
                                                <StatusChip
                                                    label={ogrenci.sinif}
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        </Grid>
                                    )}
                                    
                                    <Grid item xs={isMobile ? 6 : 3}>
                                        <StatusSelect fullWidth>
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={dersDurumlari[ogrenci.ogrenciId] || ''}
                                                onChange={(e) => onDurumChange(ders.id, ogrenci.ogrenciId, e.target.value)}
                                                disabled={!isYoklamaEnabled}
                                                sx={{
                                                    '.MuiSelect-select': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    },
                                                    bgcolor: dersDurumlari[ogrenci.ogrenciId] 
                                                        ? `${getStatusColor(dersDurumlari[ogrenci.ogrenciId])}10` 
                                                        : 'transparent'
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            borderRadius: theme.shape.borderRadius,
                                                            boxShadow: theme.shadows[4]
                                                        }
                                                    }
                                                }}
                                            >
                                                {YOKLAMA_DURUMLARI.map((durum) => (
                                                    <MenuItem 
                                                        key={durum.value} 
                                                        value={durum.value}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                borderRadius: '50%',
                                                                bgcolor: getStatusColor(durum.value)
                                                            }}
                                                        />
                                                        {durum.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </StatusSelect>
                                    </Grid>
                                </Grid>
                            </StudentRow>
                        ))}
                    </Box>
                </>
            )}
            
            <Box sx={{ p: 2, backgroundColor: theme.palette.background.default }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    İşaretlenen: {Object.keys(dersDurumlari).length}/{ders.ogrenciDurumListesi.length}
                                </Typography>
                                <Chip 
                                    label={`${Math.round(completionPercentage)}%`}
                                    size="small"
                                    color={completionPercentage === 100 ? "success" : "default"}
                                    variant={completionPercentage === 100 ? "filled" : "outlined"}
                                    icon={completionPercentage === 100 ? <CheckCircleIcon fontSize="small" /> : undefined}
                                />
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={completionPercentage} 
                                sx={{ 
                                    height: 6, 
                                    borderRadius: 3,
                                    bgcolor: theme.palette.grey[200],
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: getProgressColor()
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <ActionButton
                            variant="contained"
                            color="primary"
                            startIcon={kayitYapiliyor ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            onClick={onYoklamaAl}
                            disabled={!isYoklamaEnabled || kayitYapiliyor || Object.keys(dersDurumlari).length === 0}
                            sx={{ px: 3 }}
                        >
                            {kayitYapiliyor ? 'Kaydediliyor...' : 'Yoklama Al'}
                        </ActionButton>
                    </Grid>
                </Grid>
            </Box>
        </StyledPaper>
    );
};